/**
 * Everything about observing ONE mediasoup transport's connection health — this file
 * has no opinion on what a "session" is, what a "room" is, or what happens next; it
 * just watches a transport and calls back.
 *
 * Background for readers new to WebRTC: a mediasoup `Transport` wraps a browser
 * `RTCPeerConnection`. Getting from "just created" to "usable" involves two mostly
 * independent state machines running underneath it — ICE (finding a network path to the
 * peer) and DTLS (encrypting that path) — which mediasoup-client summarizes into one
 * `transport.connectionState` ("new" → "connecting" → "connected", or "failed"/
 * "disconnected"/"closed"). `wireTransportDiag` logs both the summary AND the two raw
 * sub-states (useful when the summary alone doesn't explain a stuck connection), and
 * `waitForTransportConnected` turns "connected" into a plain awaitable with a timeout.
 */

import type { types as Mediasoup } from "mediasoup-client";

export function wireTransportDiag(
  transport: Mediasoup.Transport,
  role: "send" | "recv",
  sig: (msg: string) => void,
  onFailed: ((role: string) => void) | null = null,
  onConnected: ((role: string) => void) | null = null
): void {
  transport.on("connectionstatechange", (state) => {
    if (
      state !== "connected" &&
      state !== "failed" &&
      state !== "disconnected" &&
      state !== "closed"
    ) {
      return;
    }
    sig(`[webrtc] ${role} transport state=${state} id=${transport.id}`);
    if (state === "connected" && typeof onConnected === "function") {
      onConnected(role);
    }
    if (state === "failed" && typeof onFailed === "function") {
      onFailed(role);
    }
  });
  transport.on("icegatheringstatechange", (state) => {
    sig(`[webrtc] ${role} iceGathering=${state} id=${transport.id}`);
  });
  try {
    // `handler`/`_handler`._pc reaches into mediasoup-client's undocumented internal
    // RTCPeerConnection — not part of its public API/types (handler() is public, but the
    // underlying handler class's _pc field is private). Kept as a best-effort diagnostic
    // only; the try/catch above is the real safety net if the internal shape ever changes.
    const t = transport as unknown as {
      handler?: { _pc?: RTCPeerConnection };
      _handler?: { _pc?: RTCPeerConnection };
    };
    const pc = t.handler?._pc ?? t._handler?._pc;
    if (pc && typeof pc.addEventListener === "function") {
      pc.addEventListener("iceconnectionstatechange", () => {
        sig(`[webrtc] ${role} iceConnection=${pc.iceConnectionState} id=${transport.id}`);
      });
      pc.addEventListener("connectionstatechange", () => {
        if (pc.connectionState === "failed" || pc.connectionState === "disconnected") {
          sig(`[webrtc] ${role} pc.connectionState=${pc.connectionState} id=${transport.id}`);
        }
      });
    }
  } catch {
    /* ignore */
  }
}

/**
 * Wait for a transport to reach `connectionState === "connected"`, or reject once
 * `ms` elapses. Bounds the case where ICE gathering finishes but connectivity checks
 * silently stall forever (no "failed" state ever fires) — otherwise nothing times out
 * and the caller hangs with no visible error.
 */
export function waitForTransportConnected(
  transport: Mediasoup.Transport,
  role: string,
  ms: number
): Promise<void> {
  if (transport.closed || transport.connectionState === "connected") {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    let done = false;
    const onChange = (state: Mediasoup.ConnectionState) => {
      if (done || state !== "connected") return;
      done = true;
      transport.off("connectionstatechange", onChange);
      clearTimeout(tid);
      resolve();
    };
    transport.on("connectionstatechange", onChange);
    const tid = setTimeout(() => {
      if (done) return;
      done = true;
      transport.off("connectionstatechange", onChange);
      reject(new Error(`${role} transport: не подключился за ${ms / 1000}s`));
    }, ms);
  });
}
